import type { ReactNode } from 'react';
import { SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { shopLabels } from '../../data/shopLabels';
import { plural } from '../../core/format/text';
import { btnSecondary, chipCls, inputLineCls } from '../../ui/styles';
import { FilterPanel, SkinChips } from './ShopFilters';
import { ProductGrid } from './ProductGrid';
import { AdviceTile } from './AdviceTile';
import { ShopEmpty } from './ShopEmpty';
import { ADVICE_SLOT, sorts } from './shopRules';
import type { ShopFilterControl } from './useShopFilters';
type Props = {
  f: ShopFilterControl;
  onOpenFilters: () => void;
  /** Sluit het vlak af, onder het raster (de beloftes). */
  footer?: ReactNode;
};

/** Anker van de collectie: de hoofdknop "Kies op je huidvraag" springt hierheen, naar de huidfilters. */
export const COLLECTION_ID = 'collectie';

/**
 * De collectie: filterkolom (desktop), zoeken, sorteren, actieve filters en de grid.
 * Een licht vlak met ronde bovenhoeken dat over de zandkleurige opening schuift (zelfde gebaar als
 * het werkwijze-blok op Home): zo leest de collectie als de toonbank, en vallen de blush-beelden
 * van de producten op.
 */
export function ShopCollection({
  f,
  onOpenFilters,
  footer
}: Props) {
  const count = f.visible.length;
  return <section id={COLLECTION_ID} className="relative z-raised -mt-6 scroll-mt-20 rounded-t-[2rem] bg-shell pb-14 pt-8 shadow-[0_-24px_48px_-28px_rgba(36,31,28,0.25)] lg:-mt-12 lg:rounded-t-[3rem] lg:pb-16 lg:pt-14" aria-label="Collectie" data-id="element-7088" data-mp="3abehc92voqqx">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7089" data-mp="5wn2ehpxs3f0">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-7090" data-mp="3o3b6c4992kj3">
          {/* Desktop: vaste filterkolom */}
          <aside className="hidden lg:col-span-3 lg:block" aria-label="Filters" data-id="element-7091" data-mp="3t1359i8bb6ws">
            <div className="lg:sticky lg:top-28" data-id="element-7092" data-mp="2cn3bkp028dhz">
              <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} data-id="element-7093" data-mp="2cmbd96f2w1o0" />
              {f.hasFilters && <button type="button" onClick={f.clear} className="mt-8 inline-flex items-center gap-1.5 text-[13px] text-clay transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7094" data-mp="1xjjrgdefcu79">
                  <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7095" data-mp="1jt7x07udajcy" />
                  Alle filters wissen
                </button>}
            </div>
          </aside>

          {/* min-w-0: anders rekt het scrollrijtje hieronder de kolom op tot buiten het scherm. */}
          <div className="min-w-0 lg:col-span-9" data-id="element-7096" data-mp="15tfayptscrbb">
            {/* Mobiel: huidtype direct zichtbaar, rest in een lade */}
            <div className="-mx-6 mb-5 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] lg:hidden [&::-webkit-scrollbar]:hidden" data-id="element-7097" data-mp="10uvp0pf6zfiy">
              <SkinChips skin={f.skin} setSkin={f.setSkin} data-id="element-7098" data-mp="306on6pdpuqx9" />
            </div>

            <div className="flex items-center gap-4" data-id="element-7099" data-mp="2v9ofyb54ri4k">
              <div className="relative flex-1" data-id="element-7100" data-mp="32rw1ezbiuzuh">
                <SearchIcon className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-7101" data-mp="32r340oygdmp0" />
                <label htmlFor="shop-search" className="sr-only" data-id="element-7102" data-mp="3chxslzz68ydm">
                  Zoek in de collectie
                </label>
                <input id="shop-search" type="search" value={f.query} onChange={e => f.setQuery(e.target.value)} placeholder="Zoek op product, ingrediënt of huidtype" className={`${inputLineCls} w-full py-3 pl-7 pr-10 [&::-webkit-search-cancel-button]:hidden`} data-id="element-7103" data-mp="3hfqvok56hc9o" />
                {f.query && <button type="button" onClick={() => {
                f.setQuery('');
                // Focus terug in het veld: de knop verdwijnt, anders valt de focus naar het begin van de pagina.
                document.getElementById('shop-search')?.focus();
              }} aria-label="Zoekopdracht wissen" className="absolute -right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-7104" data-mp="2xucl7tv9kczw">
                    <XIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7105" data-mp="gj6oxzn9m1i7" />
                  </button>}
              </div>
              <button type="button" onClick={onOpenFilters} aria-haspopup="dialog" className={`${btnSecondary} shrink-0 lg:hidden`} data-id="element-7106" data-mp="plpmq5c71nxl">
                <SlidersHorizontalIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7107" data-mp="zc0szyjco97n" />
                Filters
                {f.extraFilterCount > 0 && <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[12px] text-shell" data-id="element-7108" data-mp="199f8nvxg8tyw">
                    <span aria-hidden="true" data-id="element-7109" data-mp="1ryr4aavene8a">{f.extraFilterCount}</span>
                    <span className="sr-only" data-id="element-7110" data-mp="1wx2cyi9yriox">, {f.extraFilterCount} actief</span>
                  </span>}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-b border-line pb-4" data-id="element-7111" data-mp="1iz40xjj47ycl">
              <p className="text-[14px] text-muted" aria-live="polite" data-id="element-7112" data-mp="196ya4d4lnmyz">
                <span aria-hidden="true" data-id="element-7113" data-mp="195n7igmw4l9g">{plural(count, 'product', 'producten')}</span>
                <span className="sr-only" data-id="element-7114" data-mp="1dz983afs4yow">{shopLabels.results(count)}</span>
              </p>
              <div className="flex items-center gap-2" data-id="element-7115" data-mp="107l1tns7p4fu">
                <label htmlFor="sort" className="text-[13px] text-muted" data-id="element-7116" data-mp="147lmvr9tgdvv">
                  Sorteren
                </label>
                <select id="sort" value={f.sort} onChange={e => {
                const next = sorts.find(s => s === e.target.value);
                if (next) f.setSort(next);
              }} className={`${inputLineCls} min-h-[44px] py-2 sm:min-h-0 sm:py-1`} data-id="element-7117" data-mp="q956f8m5j90j">
                  {sorts.map(s => <option key={s} value={s} data-id="element-7118" data-mp="2o4u62dkpi2la">
                      {s}
                    </option>)}
                </select>
              </div>
            </div>

            {f.activeChips.length > 0 && <ul className="mt-4 flex flex-wrap gap-2" aria-label="Actieve filters" data-id="element-7119" data-mp="3fufd78trgpet">
                {f.activeChips.map(c => <li key={c.label} data-id="element-7120" data-mp="36ua7d347tz2r">
                    <button type="button" onClick={c.onRemove} className={`${chipCls(true)} inline-flex items-center gap-1.5`} data-id="element-7121" data-mp="36t8v4vbgr6jg">
                      {c.label}
                      <span className="sr-only" data-id="element-7122" data-mp="1qgaay24kwrk9">, filter verwijderen</span>
                      <XIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-7123" data-mp="1qez7k3f1plto" />
                    </button>
                  </li>)}
              </ul>}

            {count === 0 ? <ShopEmpty query={f.query} onClear={f.clear} data-id="element-7124" data-mp="1l93wznvodel6" /> : <>
                <ProductGrid products={f.shown} insert={count > ADVICE_SLOT ? {
              index: ADVICE_SLOT,
              node: <AdviceTile data-id="element-7126" data-mp="1v3ckty9rgjd0" />
            } : undefined} data-id="element-7125" data-mp="1q8os1n7q52sm" />
                {f.hasMore && <div className="mt-14 flex flex-col items-center gap-3 border-t border-line pt-10" data-id="element-7127" data-mp="1z2ldezt2eb6z">
                    <p className="text-[14px] text-muted" data-id="element-7128" data-mp="2403e62wxhk3w">
                      {f.limit} van {count} producten
                    </p>
                    <button type="button" onClick={f.showMore} className={btnSecondary} data-id="element-7129" data-mp="2dzu925pei14i">
                      Toon meer producten
                    </button>
                  </div>}
              </>}
          </div>
        </div>
        {footer && <div className="mt-16 lg:mt-20" data-id="element-7130" data-mp="jo73bo4z0g9c">{footer}</div>}
      </div>
    </section>;
}